import {
  useDeleteConditions,
  useMutateActivateDraftedConditions,
  useMutatePostCalculusConditions,
} from '@retail/calculus/data-access';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { useToast } from '@retail/hooks';
import { getISO_8601Date } from '@retail/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  CreateCalculusConditions,
  ConditionBinding,
  CreateConditionContent,
} from '@retail/calculus/types';
import { useCallback } from 'react';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';

export function useConditionActions() {
  const t = useCalculusTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    selectedOrgUnitAssortment: { draftAssortmentId, assortmentId },
  } = useSelectedOrgUnit();

  const handleInvalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['conditions'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['draft-conditions'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['product-groups-hierarchy'] });
    queryClient.invalidateQueries({ queryKey: ['assortmentItems'] });
    queryClient.invalidateQueries({ queryKey: ['assortment-prices'] });
  }, [queryClient]);

  const { mutateAsync: mutatePostPurchaseChanges, isPending: isLoadingPostChanges } =
    useMutatePostCalculusConditions({ assortmentId: draftAssortmentId });

  const { mutateAsync: activateDraftCondition, isPending: activateIsLoading } =
    useMutateActivateDraftedConditions({ assortmentId });

  const { mutateAsync: removeConditions } = useDeleteConditions();

  const removeCondition = (ids: number[], action: 'deactivate' | 'delete') => {
    removeConditions({ body: ids })
      .then(() => {
        toast.success(
          action === 'deactivate'
            ? t('calculus.messages.conditionDeactivated')
            : t('calculus.messages.conditionDeleted')
        );
        handleInvalidateQueries();
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('calculus.error.generic'));
        throw err;
      });
  };

  const createOrUpdateCondition = ({
    content,
    bindingValue,
    validFrom,
  }: {
    content: CreateConditionContent;
    bindingValue: ConditionBinding | ConditionBinding[];
    validFrom: Date;
  }) => {
    const bindings = Array.isArray(bindingValue) ? bindingValue : [bindingValue];
    return mutatePostPurchaseChanges({
      body: bindings.map((binding) => ({
        content,
        binding,
        validFrom: getISO_8601Date(validFrom),
      })),
    })
      .then(() => {
        toast.success(t('calculus.actions.conditionCreateInDrawer'));
        handleInvalidateQueries();
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('calculus.error.generic'));
        throw err;
      });
  };

  const createMultipleConditions = async (conditions: CreateCalculusConditions[]) => {
    return mutatePostPurchaseChanges({ body: conditions })
      .then(() => {
        toast.success(t('calculus.actions.conditionCreateInDrawer'));
        handleInvalidateQueries();
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('calculus.error.generic'));
        throw err;
      });
  };

  const activateDraft = (conditionIds: number[]) => {
    activateDraftCondition({
      body: conditionIds,
    })
      .then(() => {
        toast.success(t('calculus.messages.success'));
        handleInvalidateQueries();
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('calculus.error.generic'));
      });
  };

  return {
    createOrUpdateCondition,
    createMultipleConditions,
    removeCondition,
    activateDraft,
    isLoadingPostChanges,
    activateIsLoading,
  };
}
